import QRCode from 'qrcode';
import { BarcodeLabelData } from '../services/printer.service';
import {
  PRODUCT_LABEL_DOTS_PER_MM,
  PRODUCT_LABEL_HEIGHT_DOTS,
  PRODUCT_LABEL_MARGIN_LEFT_DOTS,
  PRODUCT_LABEL_WIDTH_DOTS
} from '../services/label-spec';

export interface RetailLabelRaster {
  width: number;
  height: number;
  rasterBytes: number[];
}

const TAMIL_FONT = '"Nirmala UI", "Latha", "Tamil MN", "Noto Sans Tamil", sans-serif';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function formatMoney(value: number | undefined): string {
  const n = Number(value) || 0;
  return n > 0 ? n.toFixed(2) : '-';
}

function currencyPrefix(symbol?: string): string {
  const s = (symbol || 'Rs').trim();
  if (/rs|₹/i.test(s)) {
    return 'Rs';
  }
  return s;
}

function canvasToRasterBytes(canvas: HTMLCanvasElement): RetailLabelRaster {
  const width = canvas.width;
  const height = canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx || width <= 0 || height <= 0) {
    return { width: 0, height: 0, rasterBytes: [] };
  }

  const imageData = ctx.getImageData(0, 0, width, height).data;
  const bytesPerRow = width / 8;
  const rasterBytes: number[] = [];

  for (let y = 0; y < height; y++) {
    for (let xByte = 0; xByte < bytesPerRow; xByte++) {
      let byte = 0;
      for (let bit = 0; bit < 8; bit++) {
        const x = (xByte * 8) + bit;
        const index = ((y * width) + x) * 4;
        const r = imageData[index];
        const g = imageData[index + 1];
        const b = imageData[index + 2];
        const alpha = imageData[index + 3];
        const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);
        const isBlack = alpha > 10 && luminance < 190;
        if (isBlack) {
          byte |= (0x80 >> bit);
        }
      }
      rasterBytes.push(byte);
    }
  }

  return { width, height, rasterBytes };
}

function clipText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) {
    return text;
  }
  let clipped = text;
  while (clipped.length > 1 && ctx.measureText(clipped + '…').width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return clipped + '…';
}

export async function renderRetailLabelCanvas(label: BarcodeLabelData): Promise<HTMLCanvasElement> {
  const width = PRODUCT_LABEL_WIDTH_DOTS;
  const height = PRODUCT_LABEL_HEIGHT_DOTS;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  const leftPad = PRODUCT_LABEL_MARGIN_LEFT_DOTS;
  const rightPad = Math.round(0.5 * PRODUCT_LABEL_DOTS_PER_MM);
  const contentWidth = width - leftPad - rightPad;

  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);

  let y = 4;
  const headerH = 38;
  const logoSize = 32;
  const logoX = leftPad;
  const logoY = y + 2;

  if (label.companyLogo) {
    try {
      const logo = await loadImage(label.companyLogo);
      ctx.save();
      ctx.beginPath();
      ctx.arc(logoX + logoSize / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);
      ctx.restore();
    } catch {
      // skip logo if image fails
    }
  }

  const shopX = logoX + logoSize + 6;
  const shopW = width - shopX - rightPad;
  const companyName = (label.companyName || '').trim();
  if (companyName) {
    const boxH = 24;
    ctx.fillStyle = '#F97316';
    ctx.fillRect(shopX, y + 4, shopW, boxH);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `bold 15px ${TAMIL_FONT}`;
    ctx.textBaseline = 'middle';
    const shopText = clipText(ctx, companyName, shopW - 8);
    ctx.fillText(shopText, shopX + 4, y + 4 + boxH / 2);
  }

  const slogan = (label.companySlogan || '').trim();
  if (slogan) {
    ctx.fillStyle = '#15803D';
    ctx.font = `11px ${TAMIL_FONT}`;
    ctx.textBaseline = 'top';
    const sloganText = clipText(ctx, slogan, shopW);
    ctx.fillText(sloganText, shopX, y + 30);
  }

  y += headerH;
  ctx.fillStyle = '#000000';
  ctx.font = `bold 15px Arial, ${TAMIL_FONT}`;
  ctx.textBaseline = 'top';
  const productName = clipText(ctx, (label.name || '').toUpperCase(), contentWidth);
  ctx.fillText(productName, leftPad, y);
  y += 20;

  const codeValue = (label.sku || label.barcode || '').trim();
  const qrSize = 84;
  const qrX = leftPad;
  const qrY = y;

  try {
    const qrCanvas = document.createElement('canvas');
    await QRCode.toCanvas(qrCanvas, label.barcode, {
      width: qrSize,
      margin: 0,
      errorCorrectionLevel: 'M'
    });
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
  } catch {
    ctx.strokeStyle = '#000000';
    ctx.strokeRect(qrX, qrY, qrSize, qrSize);
    ctx.font = '9px Arial';
    ctx.fillText('QR', qrX + 30, qrY + 34);
  }

  const priceX = qrX + qrSize + 8;
  const priceW = width - priceX - rightPad;
  const curr = currencyPrefix(label.currencySymbol);
  const mrp = formatMoney(label.mrp);
  const sale = formatMoney(label.price);

  ctx.fillStyle = '#000000';
  ctx.textBaseline = 'top';
  ctx.font = 'bold 12px Arial';
  ctx.fillText(`CODE: ${clipText(ctx, codeValue, priceW - 8)}`, priceX, qrY + 2);
  ctx.fillText(`MRP : ${mrp}`, priceX, qrY + 20);
  ctx.font = 'bold 16px Arial';
  ctx.fillText(`${curr} : ${sale}`, priceX, qrY + 42);

  return canvas;
}

export async function renderRetailLabelRaster(label: BarcodeLabelData): Promise<RetailLabelRaster> {
  const canvas = await renderRetailLabelCanvas(label);
  return canvasToRasterBytes(canvas);
}

export function retailLabelRasterToEscPos(raster: RetailLabelRaster): number[] {
  if (!raster.width || !raster.height || !raster.rasterBytes.length) {
    return [];
  }
  const bytesPerRow = raster.width / 8;
  const xL = bytesPerRow & 0xFF;
  const xH = (bytesPerRow >> 8) & 0xFF;
  const yL = raster.height & 0xFF;
  const yH = (raster.height >> 8) & 0xFF;
  return [0x1D, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...raster.rasterBytes];
}

export function retailLabelRasterToTsplHex(raster: RetailLabelRaster): string {
  return raster.rasterBytes.map(b => b.toString(16).padStart(2, '0').toUpperCase()).join('');
}
